import { type AppManagementArea, appManagementAreas } from 'paths'
import { useLocation } from 'react-router-dom'

import { useAppURLParams } from './useAppURLParams'

/**
 * Extract the current app-management area sub-path (e.g. "collections",
 * "settings/general") from the URL so it can be preserved when switching
 * databases. The app slug prefix is built from the matched route params rather
 * than searched for in the pathname, so collisions like /acme/acme/settings
 * (organization slug equal to app slug) still resolve correctly. Resource IDs
 * in deep links (e.g. devices/:peerKey) are dropped by keeping only the
 * longest prefix that is a valid AppManagementArea.
 */
export function useCurrentAppArea(): AppManagementArea | undefined {
  const params = useAppURLParams()
  const location = useLocation()

  const slug = params.appSlug
  if (!slug) return undefined
  const prefix = params.organizationSlug
    ? `/${params.organizationSlug}/${slug}/`
    : params.userId
      ? `/u/${params.userId}/${slug}/`
      : `/database/${slug}/`
  if (!location.pathname.startsWith(prefix)) return undefined
  const segments = location.pathname
    .slice(prefix.length)
    .replace(/\/$/, '')
    .split('/')
  for (let length = segments.length; length > 0; length--) {
    const candidate = segments.slice(0, length).join('/')
    if ((appManagementAreas as readonly string[]).includes(candidate)) {
      return candidate as AppManagementArea
    }
  }
  return undefined
}
